import { observer } from 'mobx-react-lite';
import ComposeComponentProps from './ComposeComponentProps';
import RecipientPanel from './RecipientPanel';
import SubjectLine from './SubjectLine';
import { ComposeControl } from 'owa-mail-compose-store';
import canModifyRecipients from 'owa-mail-compose-actions/lib/utils/canModifyRecipients';
import * as React from 'react';
import { getAllRecipients } from 'owa-mail-compose-actions/lib/utils/getAllRecipientsAsEmailAddressStrings';
import { MeetingDetails } from 'owa-meeting-message';
import { ComposeInfoBar } from 'owa-mail-compose-infobar-view';
import { ITextField } from '@fluentui/react/lib/TextField';

const styles = require('./ComposeTop.scss');
const classNames = require('classnames/bind').bind(styles);

export interface ComposeTopProps extends ComposeComponentProps {
    senderPersona?: JSX.Element;
}

const composeTopRecipientPanelFocusTargets: ComposeControl[] = [
    ComposeControl.RecipientWellTo,
    ComposeControl.RecipientWellCc,
    ComposeControl.RecipientWellBcc,
];

export interface ComposeTopHandle {
    trySetFocus(controlToFocus: ComposeControl): void;
}

export default observer(
    function ComposeTop(props: ComposeTopProps, ref: React.Ref<ComposeTopHandle>) {
        const subjectLine = React.useRef<ITextField>();
        const recipientPanel = React.useRef<RecipientPanel>();
        React.useImperativeHandle(
            ref,
            () => ({
                trySetFocus(controlToFocus: ComposeControl): void {
                    if (composeTopRecipientPanelFocusTargets.indexOf(controlToFocus) !== -1) {
                        recipientPanel.current?.focus(
                            controlToFocus as
                                | ComposeControl.RecipientWellTo
                                | ComposeControl.RecipientWellCc
                                | ComposeControl.RecipientWellBcc
                        );
                    } else if (controlToFocus == ComposeControl.SubjectLine) {
                        subjectLine.current?.focus();
                    }
                },
            }),
            []
        );
        const { viewState, senderPersona, sxsId } = props;
        const {
            infoBarIds,
            isInlineCompose,
            showCompactRecipientWell,
            fromViewState,
            ccRecipientWell,
            bccRecipientWell,
            meetingRequestItem,
        } = viewState;
        const containerClassName = classNames(styles.topContainer, {
            isInlineCompose: isInlineCompose,
        });
        const infoBarLength = (infoBarIds || []).length;
        const recipientCount = getAllRecipients(viewState).length;
        // There is possibly infobar existing, when
        // 1. infoBarIds.length > 0, OR
        // 2. recipient count > 0
        const possiblyHasInfoBar = infoBarLength + recipientCount > 0;
        return (
            <div className={containerClassName} key="composeTopDiv">
                {possiblyHasInfoBar && <ComposeInfoBar viewState={viewState} />}
                <RecipientPanel
                    isReadOnly={!canModifyRecipients(viewState)}
                    showCompactRecipientWell={showCompactRecipientWell}
                    isFromShown={fromViewState.isFromShown}
                    ccRecipientWell={ccRecipientWell}
                    bccRecipientWell={bccRecipientWell}
                    composeViewState={viewState}
                    senderPersona={senderPersona}
                    ref={ref => (recipientPanel.current = ref)}
                    sxsId={sxsId}
                />
                {!isInlineCompose && (
                    <SubjectLine
                        composeViewState={viewState}
                        ref={ref => (subjectLine.current = ref)}
                    />
                )}
                {meetingRequestItem && (
                    <div className={styles.meetingDetailsContainer}>
                        <MeetingDetails item={meetingRequestItem} isForward={true} />
                    </div>
                )}
            </div>
        );
    },
    { forwardRef: true }
);
